import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { DEFAULT_LANGUAGE, LANGUAGES, translate } from './translations';

// Kunci sendiri, TIDAK memakai `I18N_LANGUAGE` milik i18next global — supaya
// pilihan bahasa pengunjung publik tidak mengubah bahasa aplikasi ber-login.
const STORAGE_KEY = 'PUBLIC_LANDING_LANG';

const SUPPORTED = LANGUAGES.map(item => item.code);

const readStoredLanguage = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && SUPPORTED.includes(stored)) return stored;

    // Belum pernah memilih: ikuti bahasa peramban bila didukung.
    const browser = (window.navigator?.language || '').toLowerCase();
    if (browser.startsWith('id')) return 'id';
    if (browser.startsWith('en')) return 'en';
  } catch (err) {
    // localStorage bisa dilarang (mode privat / kebijakan peramban).
    // Abaikan dan pakai bahasa bawaan.
  }
  return DEFAULT_LANGUAGE;
};

const PublicLanguageContext = createContext(null);

export const PublicLanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(readStoredLanguage);

  const setLanguage = useCallback(code => {
    if (!SUPPORTED.includes(code)) return;
    setLanguageState(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch (err) {
      // Gagal menyimpan bukan alasan untuk membatalkan pergantian bahasa.
    }
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t: (key, params) => translate(language, key, params),
    }),
    [language, setLanguage]
  );

  return (
    <PublicLanguageContext.Provider value={value}>
      {children}
    </PublicLanguageContext.Provider>
  );
};

/**
 * Dipakai komponen halaman publik. Bila dipanggil di luar provider, kembalikan
 * bahasa bawaan supaya komponen tetap bisa dirender (tidak melempar error).
 */
export const usePublicLanguage = () => {
  const context = useContext(PublicLanguageContext);
  if (context) return context;

  return {
    language: DEFAULT_LANGUAGE,
    setLanguage: () => {},
    t: (key, params) => translate(DEFAULT_LANGUAGE, key, params),
  };
};

export default PublicLanguageContext;
